import { useEffect, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import { AUTH_QUERY_POLICY } from "@/lib/authQueryPolicy";
import { buildSessionExpiredLoginPath } from "@shared/session-expiration";
import { TrackLabsLoader } from "@/components/TrackLabsLoader";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requiredPage?: string;
}

// Map URL paths to page names for permission checking
const PATH_TO_PAGE_MAP: { [key: string]: string } = {
	"/app": "Welcome",
  "/repair-center": "Admin.RepairCenter",
  "/alarm-finder": "Alarm",
  "/all-locations": "Alarm",
  "/map": "Alarm",
  "/statistics": "Analytics",
  "/trigger-log": "Analytics",
  "/leaderboard": "Leader",
  "/admin": "Admin",
  "/import": "Admin",
  "/export-downloads": "Admin",
};

// Routes that should be publicly accessible without permission checks
const PUBLIC_ROUTES = ["/profile", "/directory", "/admin"];

export function ProtectedRoute({ children, requireAdmin = false, requiredPage }: ProtectedRouteProps) {
  const [, navigate] = useLocation();
  const [location] = useLocation();
  const [shouldRedirect, setShouldRedirect] = useState<string | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const utils = trpc.useUtils();
  const recoveryAttemptedRef = useRef(false);
  const sessionExpiredLoginPath = () => {
    const returnPath = typeof window === "undefined"
      ? location
      : `${window.location.pathname}${window.location.search}`;
    return buildSessionExpiredLoginPath(returnPath);
  };

  // Route guards must recover from a stale or interrupted session check. This
  // happens only when a protected route mounts, not while users switch User
  // Hub tabs, so it does not reintroduce tab-navigation auth churn.
  const { data: user, isLoading: userLoading, error: userError } = trpc.auth.me.useQuery(undefined, {
    ...AUTH_QUERY_POLICY,
    retry: 1,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  // Fetch user's custom role with permissions (only if user has a custom role)
  const { data: userRole, isLoading: roleLoading } = trpc.roles.getUserCustomRole.useQuery(
    user?.customRoleId ? { userId: user.id } : (null as any),
    {
      enabled: !!user?.customRoleId,
      retry: 1,
      staleTime: 60000, // Cache for 1 minute
    }
  );

  const authErrorCode = (userError as any)?.data?.code;
  const isUnauthorizedError = authErrorCode === "UNAUTHORIZED";

  useEffect(() => {
    if (!userError) {
      recoveryAttemptedRef.current = false;
      return;
    }

    // A temporary connection/server error must never leave the route on the
    // indefinite "Verifying access" screen. One fresh auth check lets a
    // recovered session continue; the fallback below gives the user a clear
    // retry and sign-in route if the service remains unavailable.
    if (isUnauthorizedError || recoveryAttemptedRef.current) return;
    recoveryAttemptedRef.current = true;
    void utils.auth.me.invalidate().finally(() => {
      void utils.auth.me.refetch();
    });
  }, [isUnauthorizedError, userError, utils]);

  useEffect(() => {
    if (userLoading || roleLoading) {
      setIsCheckingAccess(true);
      return;
    }

    setIsCheckingAccess(false);

    // Check authentication
    if (userError) {
      if (isUnauthorizedError) {
        localStorage.removeItem("isAuthenticated");
        localStorage.removeItem("userEmail");
        localStorage.removeItem("isAdmin");
        setShouldRedirect(sessionExpiredLoginPath());
        return;
      }
      return;
    }

    if (!user) {
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("isAdmin");
      setShouldRedirect(sessionExpiredLoginPath());
      return;
    }

    // Clear localStorage - rely only on session cookie
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("isAdmin");

    if ((user as any).mustChangePassword && location !== "/first-login") {
      const returnPath = `${location}${typeof window !== "undefined" ? window.location.search : ""}`;
      setShouldRedirect(`/first-login?return=${encodeURIComponent(returnPath)}`);
      return;
    }

    // Check admin requirement
    if (requireAdmin && user.role !== "admin" && user.role !== "top") {
		setShouldRedirect("/app");
      return;
    }

    // Admin users have full access to all pages
    if (user.role === 'admin' || user.role === 'top') {
      setShouldRedirect(null);
      return;
    }
    
    // Check if this is a public route that doesn't require permission checks
    const isPublicRoute = PUBLIC_ROUTES.some(route => location.startsWith(route));
    
    // /app is the authenticated workspace shell. UserHub performs the
    // role-aware tab selection inside it, so the route guard must not require
    // Welcome specifically or it can render a blank page for roles that start
    // on another permitted tab.
    if (!isPublicRoute && location !== "/app" && user.customRoleId && userRole?.permissions) {
      // Get the page name from the current location
      const currentPage = PATH_TO_PAGE_MAP[location] || location;
      
      // Check if user has permission for this page
      const hasPermission = userRole.permissions.some(
        (perm: any) => perm.page === currentPage && perm.canAccess === 1
      );

      if (!hasPermission) {
        // Find the first accessible page for this user
        const accessiblePage = userRole.permissions.find(
          (perm: any) => perm.canAccess === 1
        )?.page;
        
        // Map page names back to routes
        const pageToRouteMap: { [key: string]: string } = {
	          "Alarm": "/app",
	          "Activity": "/app",
	          "Welcome": "/app",
          "Analytics": "/statistics",
          "Leader": "/leaderboard",
	          "Schedule": "/app",
	          "Admin": "/admin",
	          "Admin.RepairCenter": "/repair-center",
	          "Messages": "/notifications",
        };

        const redirectTo = accessiblePage ? (pageToRouteMap[accessiblePage] || "/app") : "/app";
        setShouldRedirect(redirectTo);
        return;
      }
    }

    setShouldRedirect(null);
  }, [user, userError, userLoading, roleLoading, userRole, requireAdmin, location, isUnauthorizedError]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate(shouldRedirect);
    }
  }, [shouldRedirect, navigate]);

  if (userLoading || isCheckingAccess) return <TrackLabsLoader label="Verifying workspace access" />;

  if (shouldRedirect) {
    return null;
  }

  if (userError && !isUnauthorizedError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--background)" }}>
        <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-lg">
          <h1 className="text-lg font-semibold text-foreground">We could not verify your session</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your session was not changed. Please retry, or return to the sign-in page.
          </p>
          <div className="mt-5 flex justify-center gap-3">
            <button
              type="button"
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              onClick={() => {
                recoveryAttemptedRef.current = false;
                void utils.auth.me.refetch();
              }}
            >
              Retry verification
            </button>
            <button
              type="button"
              className="rounded-md bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground hover:bg-accent"
              onClick={() => navigate("/login")}
            >
              Go to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user || userError) return <TrackLabsLoader label="Loading your workspace" />;

  if (requireAdmin && user.role !== "admin" && user.role !== "top") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--background)" }}>
        <div className="flex flex-col items-center gap-3">
          <p className="text-sm text-muted-foreground">Access denied</p>
        </div>
      </div>
    );
  }

  // Final check for custom role access (skip for public routes and admin users)
  const isPublicRoute = PUBLIC_ROUTES.some(route => location.startsWith(route));
  
  if (!isPublicRoute && location !== "/app" && user.customRoleId && userRole?.permissions && user.role !== 'admin' && user.role !== 'top') {
    const currentPage = PATH_TO_PAGE_MAP[location] || location;
    const hasPermission = userRole.permissions.some(
      (perm: any) => perm.page === currentPage && perm.canAccess === 1
    );

    if (!hasPermission) {
      // Find the first accessible page for this user
      const accessiblePage = userRole.permissions.find(
        (perm: any) => perm.canAccess === 1
      )?.page;
      
      // Map page names back to routes
      const pageToRouteMap: { [key: string]: string } = {
		"Alarm": "/app",
		"Activity": "/app",
		"Welcome": "/app",
        "Analytics": "/statistics",
        "Leader": "/leaderboard",
        "Messages": "/notifications",
		"Schedule": "/app",
        "Admin": "/admin",
		"Admin.RepairCenter": "/repair-center",
      };
      
      // If user is admin, don't redirect them away from admin pages
      if (user.role === 'admin' || user.role === 'top') {
        return;
      }
      
	  const redirectTo = accessiblePage ? (pageToRouteMap[accessiblePage] || "/app") : "/app";
      
      // Redirect to the accessible page
      setTimeout(() => navigate(redirectTo), 0);
      
      return (
        <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--background)" }}>
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground">Redirecting to an accessible page...</p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}

export default ProtectedRoute;
